import { useEffect, useRef } from 'react';
import { Draggable } from '@fullcalendar/interaction';
import { Timer } from 'lucide-react';

import { cn } from '@/lib/cn';

interface Props {
  onPick: () => void;
  durationMinutes?: number;
}

/** Draggable 2-hour focus block for the Plan calendar. */
export function FocusBlockPalette({ onPick, durationMinutes = 120 }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const draggable = new Draggable(el, {
      itemSelector: '[data-focus-block-chip]',
      eventData() {
        return {
          title: 'Focus block',
          duration: { minutes: durationMinutes },
          create: false,
        };
      },
    });
    return () => draggable.destroy();
  }, [durationMinutes]);

  return (
    <div ref={ref}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2 className="text-small font-medium text-text-secondary">Focus</h2>
        <span className="text-label text-text-tertiary">{durationMinutes / 60}h block</span>
      </div>
      <div
        data-focus-block-chip
        data-minutes={durationMinutes}
        role="button"
        tabIndex={0}
        aria-label={`Focus block, ${durationMinutes} minutes. Drag onto the calendar.`}
        onClick={onPick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPick();
          }
        }}
        className={cn(
          'flex cursor-grab items-center gap-2 rounded border border-dashed border-accent-border',
          'bg-accent-quiet px-3 py-2 text-small text-accent',
          'hover:border-accent active:cursor-grabbing',
        )}
      >
        <Timer size={14} strokeWidth={1.5} aria-hidden />
        Focus block (2h)
      </div>
    </div>
  );
}
